import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, catchError, throwError, switchMap } from 'rxjs';
import { environment } from '@env/environment';
import { isJwtExpired, isJwtExpiringSoon, decodeJwt } from '@app/core/utils/jwt.utils';

export interface JwtTokenResponse {
  token: string;
  user?: any;
}

export interface JwtRefreshResponse {
  token: string;
  refreshToken?: string;
}

@Injectable({
  providedIn: 'root'
})
export class JwtBackendService {
  private readonly http = inject(HttpClient);
  private readonly backendUrl = 'http://localhost:3000';


  /**
   * Échange un token Firebase contre un JWT backend
   */
  exchangeFirebaseToken(firebaseToken: string): Observable<JwtTokenResponse> {
    if (!environment.production) {
      console.log('🔄 JWT-BACKEND: Échange token Firebase...');
    }
    
    return this.http.post<JwtTokenResponse>(`${this.backendUrl}/authentication/token`, {
      idToken: firebaseToken
    }).pipe(
      map((response) => {
        if (!environment.production) {
          console.log('✅ JWT-BACKEND: Token échangé avec succès');
        }
        return response;
      }),
      catchError((error) => {
        console.error('❌ JWT-BACKEND: Erreur échange token:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Refresh un JWT avec le refresh token Firebase
   */
  refreshJwtToken(firebaseRefreshToken: string): Observable<JwtRefreshResponse> {
    if (!environment.production) {
      console.log('🔄 JWT-BACKEND: Refresh du JWT...');
    }
    
    // D'abord refresh le token Firebase
    return this.refreshFirebaseToken(firebaseRefreshToken).pipe(
      // Puis l'échanger contre un nouveau JWT
      switchMap((firebaseResponse) => this.exchangeFirebaseToken(firebaseResponse.id_token)),
      map((jwtResponse) => ({
        token: jwtResponse.token,
        refreshToken: firebaseRefreshToken // Garder le même refresh token
      })),
      catchError((error) => {
        console.error('❌ JWT-BACKEND: Erreur refresh:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Récupère les données utilisateur avec JWT
   */
  getCurrentUser(jwtToken: string): Observable<any> {
    return this.http.get<any>(`${this.backendUrl}/users/me`, {
      headers: { 'Authorization': `Bearer ${jwtToken}` }
    }).pipe(
      catchError((error) => {
        console.error('❌ JWT-BACKEND: Erreur récupération utilisateur:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Stocke les tokens JWT dans localStorage
   */
  storeTokens(jwtToken: string, refreshToken?: string): void {
    if (!environment.production) {
      console.log('💾 JWT-BACKEND: Stockage des tokens');
    }
    localStorage.setItem('jwtToken', jwtToken);
    if (refreshToken) {
      localStorage.setItem('jwtRefreshToken', refreshToken);
    }
  }

  /**
   * Récupère le JWT depuis localStorage
   */
  getStoredJwt(): string | null {
    return localStorage.getItem('jwtToken');
  }

  /**
   * Récupère le refresh token depuis localStorage
   */
  getStoredRefreshToken(): string | null {
    return localStorage.getItem('jwtRefreshToken');
  }

  /**
   * Vérifie si le JWT stocké est valide
   */
  isStoredJwtValid(): boolean {
    const jwt = this.getStoredJwt();
    return jwt ? !isJwtExpired(jwt) : false;
  }

  /**
   * Vérifie si le JWT expire bientôt
   */
  isStoredJwtExpiringSoon(thresholdSeconds: number = 300): boolean {
    const jwt = this.getStoredJwt();
    return jwt ? isJwtExpiringSoon(jwt, thresholdSeconds) : true;
  }

  /**
   * Nettoie les tokens stockés
   */
  clearStoredTokens(): void {
    if (!environment.production) {
      console.log('🧹 JWT-BACKEND: Nettoyage des tokens');
    }
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('jwtRefreshToken');
  }

  /**
   * Refresh le token Firebase (méthode privée)
   */
  private refreshFirebaseToken(refreshToken: string): Observable<any> {
    const url = `https://securetoken.googleapis.com/v1/token?key=${environment.firebaseConfig.apiKey}`;
    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('refresh_token', refreshToken);

    return this.http.post<any>(url, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  }
}